/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { Link as ScrollLink } from 'react-scroll';
import classNames from 'classnames';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';

import clsx from 'clsx';
import { Trans } from 'react-i18next';
import Tooltip from '../../../Tooltip';
import { CardContext } from '../../CardContext';
import { scrollerSettings } from '../../../../../settings';
import { selectVersion } from '../../../../../redux/features/configuration/configuration.slice';
import { dataTestId } from '../../../../../shared/utils/versionUtils';
import { checkTitleAndRemoveWhitespaces } from '../../../../../shared/utils/stringUtils';

const useStyles = makeStyles(theme => ({
  linkNumber: {
    '& > p': {
      backgroundColor: theme.palette.iconBackground,
      minWidth: '2.188rem',
      borderRadius: '0.188rem',
      padding: '0.125rem 0.375rem',
    },
  },
  scrollLink: {
    cursor: 'pointer',
    color: `${theme.palette.linkColour}`,
    textDecoration: 'underline',
    '&:hover': {
      color: theme.palette.buttonTextHover,
    },
    '&:focus-visible': {
      outline: `calc(2rem / 16) solid ${theme.palette.buttonTextHover}`,
    },
  },
  title: {
    flex: 1,
  },
}));

/**
 * Custom summary entry with extra features from the regular summary entry.
 * @param type: like "attachment"/"fee"
 * @param title: string title
 * @param value: string value
 * @param EntryIcon: optional icon
 * @param hasDivider: divider indicator
 * @param link: indicates if the value is a link
 * @param linkTo:
 * @param readOnly: read only boolean
 * @param hideLinkTo - hides linkTo
 */
export default function CustomEntry({
  type,
  title,
  value,
  icon: EntryIcon,
  hasDivider,
  link,
  linkTo = '',
  readOnly,
  hideLinkTo,
}) {
  const { classes } = useContext(CardContext);
  const titleClass = classes[`${type}EntryKey`];
  const valueClass = classes[`${type}EntryValue`];
  const selectedVersion = useSelector(selectVersion);
  const entryTitle = checkTitleAndRemoveWhitespaces(title);
  const localClasses = useStyles();
  const isFeesCard = type === 'fees';
  return (
    <>
      <Grid item container spacing={2} className={classes.content}>
        {!hideLinkTo && EntryIcon && (
          <Grid item xs={1} className={classes.entryIcon}>
            <EntryIcon />
          </Grid>
        )}
        {title && (
          <Grid item className={clsx(link && localClasses.linkNumber, isFeesCard && localClasses.title)}>
            <Typography
              variant="body2"
              display="inline"
              className={titleClass}
              {...dataTestId({
                value: `${type}_summary_${entryTitle}-title--${selectedVersion.value}`,
              })}
            >
              <Trans i18nKey="title" title={title}>
                {title}
              </Trans>
            </Typography>
          </Grid>
        )}
        {value && (
          <Grid item className={classNames(classes.entryValueGridItem, link && localClasses.linkTitle)}>
            {link ? (
              <ScrollLink
                disabled={readOnly}
                activeClass="active"
                className={localClasses.scrollLink}
                to={readOnly ? null : linkTo}
                smooth={scrollerSettings.smooth}
                offset={scrollerSettings.offset}
                delay={scrollerSettings.delay}
                duration={scrollerSettings.duration}
                tabIndex={0}
              >
                {value}
              </ScrollLink>
            ) : (
              <Tooltip title={value} placement="top" arrow disableHoverListener={value.length < 20}>
                <>
                  <Typography
                    variant="body2"
                    noWrap
                    display="inline"
                    className={valueClass}
                    {...dataTestId({
                      value: `${type}_summary_${entryTitle}-value--${selectedVersion.value}`,
                    })}
                  >
                    {value}
                  </Typography>
                </>
              </Tooltip>
            )}
          </Grid>
        )}
        {hasDivider && (
          <Grid item xs={12} className={classes.divider}>
            <Divider />
          </Grid>
        )}
      </Grid>
    </>
  );
}
